// src/views/EventsList.jsx
import React, {useState, useEffect} from "react";
import {
    HomeOutlined,
    SearchOutlined,
    PlusOutlined,
    CalendarOutlined,
    ReloadOutlined,
    FileTextOutlined,
} from "@ant-design/icons";
import {
    Breadcrumb,
    Card,
    Spin,
    Alert,
    Input,
    Button,
    Pagination,
    DatePicker,
    Empty,
    message,
    Row,
    Col,
    Space,
    Typography,
    Divider,
} from "antd";
import {Link} from "react-router-dom";
import EventCard from "@/components/Events/EventCard";
import {getAllEvents, markEventAsRead, markEventAsUnread} from "@/api/eventApi";
import {decodeToken} from "@/utils/tokenCookies";
import "./EventsList.css";

const {RangePicker} = DatePicker;
const {Title, Text} = Typography;

const EventsList = () => {
    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [total, setTotal] = useState(0);
    const [search, setSearch] = useState("");
    const [searchInput, setSearchInput] = useState("");
    const [dateRange, setDateRange] = useState(null);

    const token = decodeToken();
    const isAdmin = token?.roles?.includes("admin") || token?.roles?.includes("superadmin");

    useEffect(() => {
        if (isAdmin) {
            fetchEvents();
        }
    }, [page, pageSize, search, dateRange]);

    const fetchEvents = async () => {
        setLoading(true);
        setError(null);
        try {
            const params = {
                page,
                page_size: pageSize,
            };

            if (search) params.search = search;

            if (dateRange && dateRange.length === 2) {
                params.from_date = dateRange[0].format("YYYY-MM-DD");
                params.to_date = dateRange[1].format("YYYY-MM-DD");
            }

            const data = await getAllEvents(params);
            setEvents(data.items || []);
            setTotal(data.total || 0);
        } catch (err) {
            console.error("Error fetching events:", err);
            setError(err.response?.data?.message || "Tadbirlarni yuklashda xatolik");
            message.error("Tadbirlarni yuklashda xatolik");
        } finally {
            setLoading(false);
        }
    };

    const handleSearch = () => {
        setSearch(searchInput);
        setPage(1);
    };

    const handleDateRangeChange = (dates) => {
        setDateRange(dates);
        setPage(1);
    };

    const handleMarkAsRead = async (eventId) => {
        try {
            await markEventAsRead(eventId);
            message.success("Tadbir o'qilgan deb belgilandi");
            fetchEvents();
        } catch {
            message.error("Xatolik yuz berdi");
        }
    };

    const handleMarkAsUnread = async (eventId) => {
        try {
            await markEventAsUnread(eventId);
            message.success("Event o'qilmagan deb belgilandi");
            fetchEvents();
        } catch {
            message.error("Xatolik yuz berdi");
        }
    };

    const handlePageChange = (newPage, newPageSize) => {
        setPage(newPage);
        if (newPageSize !== pageSize) {
            setPageSize(newPageSize);
        }
    };

    if (!isAdmin) {
        return (
            <Card style={{margin: 24}}>
                <Alert
                    message="Ruxsat yo'q"
                    description="Siz ushbu sahifaga kirish huquqiga ega emassiz. Faqat adminlar barcha tadbirlarni ko'ra oladi."
                    type="error"
                    showIcon
                />
            </Card>
        );
    }

    return (
        <div style={{padding: "24px"}}>
            {/* Breadcrumb */}
            <Breadcrumb
                items={[
                    {
                        title: (
                            <Link to="/">
                                <HomeOutlined/> Bosh sahifa
                            </Link>
                        ),
                    },
                    {title: "Barcha tadbirlar"},
                ]}
            />



            {/* Filters */}
            <Card className="my-5" bordered={true} style={{borderRadius: 12}}>
                <Row gutter={[16, 16]}>
                    <Col xs={24} md={2}>
                        <Link to="/events/create" style={{display: 'block', width: '100%'}}>
                            <Button
                                type="primary"
                                icon={<PlusOutlined/>}
                                size="middle"
                                style={{width: '100%'}}
                            >
                                Yaratish
                            </Button>
                        </Link>
                    </Col>
                    <Col xs={24} md={11}>
                        <Input
                            size="middle"
                            placeholder="Tadbir nomini qidirish..."
                            prefix={<SearchOutlined/>}
                            value={searchInput}
                            onChange={(e) => setSearchInput(e.target.value)}
                            onPressEnter={handleSearch}
                            allowClear
                        />
                    </Col>
                    <Col xs={24} md={11}>
                        <RangePicker
                            size="middle"
                            style={{width: "100%"}}
                            onChange={handleDateRangeChange}
                            placeholder={["Boshlanish sanasi", "Tugash sanasi"]}
                            format="YYYY-MM-DD"
                        />
                    </Col>

                </Row>

                {/* Statistics Section - Inside Filter Card */}
                {!loading && events.length > 0 && (
                    <>
                        <Divider style={{margin: "16px 0"}}/>
                        <Row gutter={[16, 8]} align="middle">
                            <Col>
                                <Space size="large">
                                    <Space size="small" className="d-flex align-items-center">
                                        <CalendarOutlined style={{fontSize: 18, color: "#1890ff"}}/>
                                        <div>
                                            <Text type="secondary" style={{fontSize: 13, display: "block"}}>
                                                Jami tadbirlar soni: <span className={'fw-bold fs-6'}>{total}</span>
                                            </Text>

                                        </div>
                                    </Space>

                                    <Divider type="vertical" style={{height: 40}}/>

                                    <Space size="small" className="d-flex align-items-center">
                                        <FileTextOutlined style={{fontSize: 18, color: "#52c41a"}}/>
                                        <div>
                                            <Text type="secondary" style={{fontSize: 13, display: "block"}}>
                                                Sahifada: <span className={'fw-bold fs-6'}>  {events.length}</span>
                                            </Text>

                                        </div>
                                    </Space>

                                    <Divider type="vertical" style={{height: 40}}/>

                                    {/*<Space size="small">*/}
                                    {/*  <Text type="secondary" style={{ fontSize: 12 }}>*/}
                                    {/*    Sahifa: <Text strong>{page}</Text> / <Text strong>{Math.ceil(total / pageSize)}</Text>*/}
                                    {/*  </Text>*/}
                                    {/*</Space>*/}
                                </Space>
                            </Col>

                            {(search || dateRange) && (
                                <>
                                    <Col flex="auto"/>
                                    <Col>
                                        <Button
                                            icon={<ReloadOutlined/>}
                                            onClick={() => {
                                                setSearch("");
                                                setSearchInput("");
                                                setDateRange(null);
                                                setPage(1);
                                            }}
                                            size="small"
                                        >
                                            Tozalash
                                        </Button>
                                    </Col>
                                </>
                            )}
                        </Row>
                    </>
                )}
            </Card>

            {/* Events List */}
            <Spin spinning={loading} tip="Tadbirlar yuklanmoqda..." size="large">
                {error ? (
                    <Alert message="Xatolik" description={error} type="error" showIcon/>
                ) : events.length === 0 ? (
                    <Card>
                        <Empty description="Tadbirlar topilmadi"/>
                    </Card>
                ) : (
                    <>
                        <Row gutter={[16, 16]}>
                            {events.map((event) => (
                                <Col key={event.id} xs={24} sm={24} md={12} lg={12}>
                                    <EventCard
                                        event={event}
                                        isAdmin={true}
                                        onMarkAsRead={handleMarkAsRead}
                                        onMarkAsUnread={handleMarkAsUnread}
                                    />
                                </Col>
                            ))}
                        </Row>

                        {total > pageSize && (
                            <div className="text-center mt-5">
                                <Pagination
                                    current={page}
                                    pageSize={pageSize}
                                    total={total}
                                    onChange={handlePageChange}
                                    showSizeChanger
                                    showTotal={(total) => `Jami ${total} ta tadbir`}
                                    pageSizeOptions={[10, 20, 50, 100]}
                                />
                            </div>
                        )}
                    </>
                )}
            </Spin>
        </div>
    );
};

export default EventsList;